import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, CircularProgress, Alert,
  Container, Tooltip
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import ChatIcon from '@mui/icons-material/Chat';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import api from '../../services/api';

/**
 * CallHistory - Page showing the user's past consultations.
 * Fetches from GET /api/v1/consultations/history with pagination.
 */
const CallHistory = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination
  const [rowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/consultations/history', {
        params: {
          page: page + 1, // API uses 1-indexed pages
          limit: rowsPerPage,
        },
      });
      const data = response.data;
      setConsultations(data.consultations || []);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load consultation history');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const formatDateIST = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '-';
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return (
          <Tooltip title="Video Call">
            <VideocamIcon color="secondary" fontSize="small" />
          </Tooltip>
        );
      case 'call':
        return (
          <Tooltip title="Voice Call">
            <PhoneIcon color="primary" fontSize="small" />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title="Chat">
            <ChatIcon color="info" fontSize="small" />
          </Tooltip>
        );
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'call': return 'Voice';
      default: return 'Chat';
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      completed: { color: 'success', label: 'Completed' },
      cancelled: { color: 'error', label: 'Cancelled' },
      ongoing: { color: 'info', label: 'Ongoing' },
      scheduled: { color: 'default', label: 'Scheduled' },
      connecting: { color: 'warning', label: 'Connecting' },
    };
    const config = statusMap[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
  };

  const renderRating = (rating) => {
    if (!rating || rating === 0) {
      return (
        <Typography variant="caption" color="text.secondary">-</Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating
            ? <StarIcon key={star} sx={{ fontSize: 16, color: '#faaf00' }} />
            : <StarBorderIcon key={star} sx={{ fontSize: 16, color: '#faaf00' }} />
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Consultation History
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View your past consultations, ratings, and session details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : consultations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No consultations found. Book a session to get started!
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Astrologer</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">Duration</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consultations.map((consultation) => (
                  <TableRow key={consultation.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateIST(consultation.start_time || consultation.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {consultation.astrologer?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {getTypeIcon(consultation.type)}
                        <Typography variant="caption">{getTypeLabel(consultation.type)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatDuration(consultation.duration_minutes)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {consultation.amount_paid > 0
                          ? `₹${consultation.amount_paid.toFixed(0)}`
                          : '-'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(consultation.status)}
                    </TableCell>
                    <TableCell align="center">
                      {renderRating(consultation.rating)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </Paper>
      )}
    </Container>
  );
};

export default CallHistory;
